<template>
  <div class="flex h-full gap-2 bg-white p-2">
    <div
      class="h-[114px] w-[87px] shrink-0 bg-white"
      :style="{
        filter: `brightness(${imageBrightnessFilter}%)`,
        backgroundColor: imageBackgroundColor?.hexa || 'transparent',
      }"
    >
      <UiLazyImg
        class="aspect-[87/114]"
        :src="productImages?.[0]?.url || '/images/no-image.webp'"
        :alt="product.currentTranslation?.title"
        cover
      />
    </div>
    <div
      class="flex flex-grow flex-col"
      :style="{
        alignItems: cardContentPosition === 'right' ? 'end' : 'start',
      }"
    >
      <div class="w-full">
        <NuxtLink
          :to="productLink"
          class="text-xs block pt-1 font-medium uppercase"
          @click="onTitleClick"
        >
          <span>{{ productTitle }}</span>
        </NuxtLink>

        <p
          class="ui-text-sm mt-1 flex items-center gap-1 font-normal text-primary"
        >
          <span
            v-if="isConfigurationOutOfStockPlp"
            class="text-black-600 lowercase"
          >
            {{ $t("outOfStock") }}
          </span>
          <span v-else class="text-primary">
            {{ $n(productPrice, "currency") }}
          </span>
        </p>
        <ProductMaterialSelectView
          v-if="ringSizeMaterialCategory"
          class="materials-select"
          hide-label
          :block="ringSizeMaterialCategory"
          :title="ringSizeMaterialCategory?.currentTranslation?.title"
          :selected-item="ringSizeSelectedItem"
          @filter="(value) => handleFilter(value)"
        />
      </div>
      <div class="mt-auto flex w-full gap-2">
        <UiButton
          v-if="product.default_sku"
          class="size-8 shrink-0 rounded-full bg-transparent text-primary"
          variant="clear"
          variant-type="icon"
          text="xs"
          :aria-label="$t('addToWishlist')"
          @click="updateWishlist"
        >
          <ClientOnly>
            <UiIcon
              :name="inWishlist ? 'heart-bold' : 'heart'"
              class="size-4"
              :class="[inWishlist ? 'fill-primary text-primary' : '']"
              aria-hidden="true"
            />
          </ClientOnly>
        </UiButton>
        <UiButton
          v-if="productSKU && !isConfigurationOutOfStockPlp"
          class="font-akzidenz-grotesk-pro flex-grow whitespace-nowrap px-0 text-xs font-normal"
          :loading="addToCartPending"
          :disabled="addToCartPending"
          data-e2e-btn="add-to-cart"
          @click="onAddToCart(false)"
        >
          {{ $t("addToCart") }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  CatalogData,
  ColorPickerData,
  FilteredMaterialsByCategoriesI,
  IProductMaterialFilterEvent,
  MaterialData,
} from "ecom-types";

const props = withDefaults(
  defineProps<{
    imageBrightnessFilter?: string;
    cardContentPosition?: string;
    product: CatalogData;
    productIndex: number;
    statisticItemListName?: string;
    statisticItemListId?: string;
    imageBackgroundColor?: ColorPickerData;
    productImages: any[];
    productTitle: string;
    discountValue: number;
    isConfigurationInventoryOutOfStockPlp: boolean;
    isConfigurationOutOfStockPlp: boolean;
    productPrice: number;
    productLink: string;
    ringSizeMaterialCategory: FilteredMaterialsByCategoriesI | null;
    ringSizeSelectedItem: MaterialData | null;
    showOutOfStock?: boolean;
    productSKU: string;
    addToCartPending: boolean;
    inWishlist: boolean;
    setGaSelectItemEvent: Function;
    handleFilter: (value: IProductMaterialFilterEvent) => void;
    updateWishlist: Function;
    onAddToCart: Function;
  }>(),
  {
    showOutOfStock: false,
  },
);

const { setItemListContext } = useItemListContext();

function onTitleClick() {
  setItemListContext(
    props.statisticItemListName || "",
    props.statisticItemListId || "",
  );
  props.setGaSelectItemEvent && props.setGaSelectItemEvent();
}
</script>

<style scoped>
.materials-select {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

:deep(.materials-select) select {
  font-size: 0.875rem;
  line-height: 1.25rem;
  height: fit-content;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

:deep(.materials-select) .text-cherry {
  display: none;
}

:deep(.materials-select) label {
  display: none;
}
</style>

